// firebase/hooks.ts
// Custom React hooks for Firebase operations

import { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from './config';
import type { UploadProgress } from '../types';

export const useFileUpload = () => {
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

    const uploadFile = (file: File, path: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            const storageRef = ref(storage, path);
            const uploadTask = uploadBytesResumable(storageRef, file);

            setUploadProgress({
                fileName: file.name,
                progress: 0,
                status: 'uploading',
            });

            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress({
                        fileName: file.name,
                        progress: Math.round(progress),
                        status: 'uploading',
                    });
                },
                (error) => {
                    setUploadProgress({
                        fileName: file.name,
                        progress: 0,
                        status: 'error',
                        error: error.message,
                    });
                    reject(error);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setUploadProgress({
                            fileName: file.name,
                            progress: 100,
                            status: 'complete',
                        });
                        resolve(downloadURL);
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    };

    const resetProgress = () => {
        setUploadProgress(null);
    };

    return { uploadFile, uploadProgress, resetProgress };
};